import React, { useEffect, useMemo, useState } from 'react';
import { AiOutlineLoading } from 'react-icons/ai';
import { useMediaQuery } from 'react-responsive';

import { Badge, Pagination, Select, Table } from 'design-system';

import moment from 'moment';
import { getRangeDateRefuel } from 'utils/getRangeDateRefuel';
import { getStatusType } from 'newComponents/NewNotifications/NotificationItem/util';

import {
  Container,
  Content,
  Footer,
  Header,
  HeaderTitle,
  LoadingSpin,
  StatusBadgeWrap,
  TableSection,
  TypeSelectWrap,
} from './styles';

const DESKTOP_PAGE_SIZE = 4;
const MOBILE_PAGE_SIZE = 4;

const PROCESSING_CODES = [
  'GERPEDAUT',
  'RECEBIDO',
  'AGUANA',
  'AGUCONLOG',
  'GREOSAUT',
  'RENDATCLI',
];

type SortKey = 'order' | 'forecast';
type SortDirection = 'asc' | 'desc';

type BadgeTheme = {
  label: string;
  backgroundColor: string;
  color: string;
};

interface IProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  data: any[];
  filterRequest: string;
  setFilterRequest: React.Dispatch<React.SetStateAction<string>>;
  filterSituation: string;
  setFilterSituation: React.Dispatch<React.SetStateAction<string>>;
}

const STATUS_THEMES: Record<string, Omit<BadgeTheme, 'label'>> = {
  processando: { backgroundColor: '#EDE9FE', color: '#6D28D9' },
  'em-analise': { backgroundColor: '#FFF8E6', color: '#C9920A' },
  'os-em-analise': { backgroundColor: '#FFF8E6', color: '#C9920A' },
  'os-em-atendimento': { backgroundColor: '#FFF8E6', color: '#C9920A' },
  reprogramado: { backgroundColor: '#E0F7FA', color: '#00838F' },
  agendado: { backgroundColor: '#E0F7FA', color: '#00838F' },
  realizado: { backgroundColor: '#E0F7FA', color: '#00838F' },
  confirmado: { backgroundColor: '#E3F2FD', color: '#1976D2' },
  'em-rota': { backgroundColor: '#E3F2FD', color: '#1976D2' },
  cancelado: { backgroundColor: '#FCEAE8', color: '#E53935' },
  'nao-atendido': { backgroundColor: '#FCEAE8', color: '#E53935' },
  'os-cancelada': { backgroundColor: '#FCEAE8', color: '#E53935' },
  atendido: { backgroundColor: '#E8F5E9', color: '#43A047' },
  'os-finalizada': { backgroundColor: '#E8F5E9', color: '#43A047' },
  'nao-identificado': { backgroundColor: '#F0F0F0', color: '#575757' },
};

const statusValue = (status: string): string => {
  switch (status) {
    case 'os-em-analise':
      return 'Em análise';
    case 'os-em-atendimento':
      return 'Em atendimento';
    case 'os-finalizada':
      return 'Finalizada';
    case 'os-cancelada':
      return 'Cancelado';
    case 'realizado':
    case 'agendado':
      return 'Agendado';
    case 'cancelado':
      return 'Cancelado';
    case 'reprogramado':
      return 'Reprogramado';
    case 'confirmado':
      return 'Confirmado';
    case 'em-rota':
      return 'Em rota';
    case 'nao-atendido':
      return 'Não atendido';
    case 'atendido':
      return 'Atendido';
    case 'nao-identificado':
      return 'Não identificado';
    case 'em-analise':
    default:
      return 'Em análise';
  }
};

const getOrderNumber = (item: any, filterRequest: string): string => {
  if (filterRequest === 'assistancie') {
    const value = item?.serviceOrder?.numberOs;
    return value != null && value !== '' ? String(value) : '-';
  }

  const value = item?.orderNumber;
  return value != null && value !== '' ? String(value) : '-';
};

const formatForecastDisplay = (value: string): string => {
  if (!value) return '-';

  const parts = value.split(' a ');
  if (parts.length === 2 && parts[0] === parts[1]) {
    return parts[0];
  }

  return value;
};

const getForecastValue = (item: any, filterRequest: string): string => {
  if (filterRequest === 'assistancie') {
    const date = item?.serviceOrder?.attendanceForecastDate;
    return date ? moment(date).format('DD/MM/YY') : '-';
  }

  return formatForecastDisplay(
    getRangeDateRefuel(
      item.forecastDate,
      item?.beginDate,
      item?.endDate,
      item?.suggestedDate,
      item?.agreedDate,
      null,
      null,
      null
    )
  );
};

const getSortDate = (item: any, filterRequest: string): number => {
  const date =
    filterRequest === 'assistancie'
      ? item?.serviceOrder?.attendanceForecastDate
      : item?.forecastDate ||
        item?.beginDate ||
        item?.agreedDate ||
        item?.suggestedDate;

  return date ? new Date(date).getTime() : 0;
};

const getBadgeTheme = (item: any, filterRequest: string): BadgeTheme => {
  if (filterRequest === 'refuel') {
    if (PROCESSING_CODES.includes(item?.statusCode)) {
      return {
        label: 'Processando',
        ...STATUS_THEMES.processando,
      };
    }

    const label = getStatusType(item?.statusCode, 'refuel', true);
    const slug = item?.status || 'em-analise';

    return {
      label,
      ...(STATUS_THEMES[slug] || STATUS_THEMES['em-analise']),
    };
  }

  const slug = item?.status || 'os-em-analise';

  return {
    label: statusValue(slug),
    ...(STATUS_THEMES[slug] || STATUS_THEMES['os-em-analise']),
  };
};

const renderStatusBadge = (item: any, filterRequest: string): JSX.Element => {
  const { label, backgroundColor, color } = getBadgeTheme(item, filterRequest);

  return (
    <StatusBadgeWrap>
      <Badge
        size="sm"
        backgroundColor={backgroundColor}
        color={color}
      >
        {label}
      </Badge>
    </StatusBadgeWrap>
  );
};

const MinhasSolicitacoesContent = ({
  loading,
  data,
  filterRequest,
  setFilterRequest,
  setFilterSituation,
}: IProps): JSX.Element => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    setPage(1);
    setSortKey(null);
    setSortDirection('asc');
  }, [filterRequest, data.length]);

  const handleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDirection('asc');
  };

  const sortedData = useMemo(() => {
    if (!sortKey) return data;

    return [...data].sort((first, second) => {
      let comparison = 0;

      if (sortKey === 'order') {
        const firstOrder = getOrderNumber(first, filterRequest);
        const secondOrder = getOrderNumber(second, filterRequest);
        comparison = firstOrder.localeCompare(secondOrder, undefined, {
          numeric: true,
        });
      }

      if (sortKey === 'forecast') {
        comparison =
          getSortDate(first, filterRequest) - getSortDate(second, filterRequest);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, filterRequest, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const handleTypeChange = (value: string): void => {
    setFilterRequest(value);
    setFilterSituation('all');
  };

  const typeSelect = (
    <TypeSelectWrap>
      <Select
        size="sm"
        fullWidth={isMobile}
        value={filterRequest}
        borderColor="#262626"
        onChange={(event) => {
          handleTypeChange(event.currentTarget.value);
        }}
      >
        <option value="refuel">Abastecimento</option>
        <option value="assistancie">Manutenção</option>
      </Select>
    </TypeSelectWrap>
  );

  return (
    <Container>
      <Header>
        <HeaderTitle>Meus atendimentos</HeaderTitle>
        {typeSelect}
      </Header>

      {loading ? (
        <LoadingSpin>
          <AiOutlineLoading />
        </LoadingSpin>
      ) : (
        <>
          <Content>
            <TableSection>
              <Table
                size="sm"
                headerBackgroundColor="#fff"
                headerColor="#575757"
              >
                <Table.Head>
                  <Table.Row>
                    <Table.HeaderCell
                      align="center"
                      sortable
                      sortDirection={sortKey === 'order' ? sortDirection : null}
                      onSort={() => handleSort('order')}
                    >
                      Nº do pedido
                    </Table.HeaderCell>
                    <Table.HeaderCell align="center">Status</Table.HeaderCell>
                    <Table.HeaderCell
                      align="center"
                      sortable
                      sortDirection={
                        sortKey === 'forecast' ? sortDirection : null
                      }
                      onSort={() => handleSort('forecast')}
                    >
                      {isMobile
                        ? 'Prev. de abastecimento'
                        : 'Previsão de atendimento'}
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Head>
                <Table.Body striped>
                  {paginatedData.length ? (
                    paginatedData.map((item: any) => (
                      <Table.Row key={item?.caseNumber ?? item?.orderNumber}>
                        <Table.Cell align="center">
                          {filterRequest === 'refuel'
                            ? item?.orderNumber ?? 'Processando'
                            : getOrderNumber(item, filterRequest)}
                        </Table.Cell>
                        <Table.Cell align="center">
                          {renderStatusBadge(item, filterRequest)}
                        </Table.Cell>
                        <Table.Cell align="center">
                          {getForecastValue(item, filterRequest)}
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Empty colSpan={3}>
                      Não foi encontrado nenhum item para exibir
                    </Table.Empty>
                  )}
                </Table.Body>
              </Table>
            </TableSection>
          </Content>

          {sortedData.length > 0 && (
            <Footer>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                size="sm"
                siblingCount={isMobile ? 3 : 2}
                color="#575757"
                activeBackgroundColor="#000FFF"
                activeColor="#fff"
              />
            </Footer>
          )}
        </>
      )}
    </Container>
  );
};

export default MinhasSolicitacoesContent;
